import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon, WaitingInline } from '~/components/ui';
import { useT, type Messages } from '~/i18n';
import {
  coachAnswer,
  transcribeAnswer,
  type AnswerQuality,
  type CoachVerdict,
  type InputMode,
} from '../api/voiceClient';
import { useVoiceRecorder, type RecorderProblem } from '../lib/useVoiceRecorder';
import {
  MAX_RECORDING_SECONDS,
  WARN_RECORDING_SECONDS,
  appendAnswer,
  formatSeconds,
  isRecordingSupported,
} from '../lib/voice';

/**
 * Una pregunta abierta contestada HABLANDO.
 *
 * ── LA JERARQUÍA, QUE ES TODO EL DISEÑO ────────────────────────────────────
 * El micrófono es el campo. No es un accesorio al lado de un textarea: es lo
 * primero, es grande, y es lo único que se ve hasta que hay algo que corregir.
 * "Prefiero escribir" queda debajo, discreto y siempre disponible — nunca
 * escondido, nunca en un menú, porque quien está en un bus o al lado de gente
 * necesita esa puerta sin tener que buscarla.
 *
 * El orden importa por una razón concreta: pedir "escribe qué hacías" produce
 * "recolección de café". Pedir "cuéntame un día normal" produce dos minutos con
 * la marca del tractor, las hectáreas y el turno. La hoja de vida se arma con lo
 * segundo, y la probabilidad de colocación también.
 *
 * ── EL COACHING NO REGAÑA Y NO BLOQUEA ─────────────────────────────────────
 * Cuando la respuesta sale corta, el coach REPREGUNTA con curiosidad ("¿de
 * cuántas hectáreas era la finca?") y la respuesta nueva se SUMA a la anterior.
 * Nunca dice "eso es poco", nunca condiciona el avance: "Continuar así" está
 * siempre a un toque. Máximo dos repreguntas por campo —el backend lo fuerza con
 * `prior_follow_ups`— porque una tercera deja de ser interés y se vuelve
 * interrogatorio.
 *
 * ── LO QUE NO GUARDA ───────────────────────────────────────────────────────
 * El audio. Se graba, se sube, se transcribe y se suelta: en el borrador y en
 * sessionStorage vive el TEXTO, nunca el blob. Un teléfono prestado no se queda
 * con la voz de quien lo usó antes.
 */

/** Desde cuántos caracteres libres se avisa el tope. */
const CHARS_LEFT_WARNING = 100;

interface Props {
  /** El id del textarea, para que la etiqueta del `Field` lo alcance. */
  id: string;
  /** La llave con la que viajan `answer_quality` e `input_mode`: 'duties.0'. */
  field: string;
  /**
   * La ruta con la que el backend ancla su error, cuando NO es la misma llave.
   * Los duties viajan como `duties.0` para la analítica y como
   * `last_jobs.0.duties` para el 400 de zod, y el foco del error busca la
   * segunda: sin esto, el error se pinta y nadie sabe qué campo lo causó.
   */
  dataField?: string;
  /** La pregunta tal cual se la hacemos: es lo que el coach evalúa contra. */
  question: string;
  /** La invitación a hablar. Una frase, en segunda persona, sin jerga. */
  invitation: string;
  value: string;
  maxLength: number;
  rows?: number;
  placeholder?: string;
  invalid?: boolean;
  /** Apagado donde repreguntar no tiene sentido (una lista no es un relato). */
  coach?: boolean;
  onChange: (value: string, mode: InputMode) => void;
  onQuality?: (quality: AnswerQuality) => void;
  /** Se pinta debajo del campo: el botón de "agregar a la lista", por ejemplo. */
  footer?: ReactNode;
}

export default function VoiceAnswer({
  id,
  field,
  dataField,
  question,
  invitation,
  value,
  maxLength,
  rows = 4,
  placeholder,
  invalid,
  coach = true,
  onChange,
  onQuality,
  footer,
}: Props) {
  const t = useT('screening');
  // Se resuelve una vez por montaje y no en el módulo: en las pruebas el
  // navegador falso se instala antes de renderizar, no antes de importar.
  const [supported] = useState(isRecordingSupported);

  const [openText, setOpenText] = useState(() => !supported || value.trim().length > 0);
  const [transcribing, setTranscribing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [verdict, setVerdict] = useState<CoachVerdict | null>(null);
  const [thinking, setThinking] = useState(false);

  const textRef = useRef<HTMLTextAreaElement | null>(null);
  const aliveRef = useRef(true);
  /** La última respuesta que ya evaluamos: evita repetir la llamada al salir. */
  const coachedRef = useRef('');
  /** Cuántas repreguntas van en ESTE campo. El backend corta en la segunda. */
  const followUpsRef = useRef(0);
  /** La pregunta viva: tras una repregunta, el coach evalúa contra la SUYA. */
  const askedRef = useRef(question);
  const focusOnOpenRef = useRef(false);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (openText && focusOnOpenRef.current) {
      focusOnOpenRef.current = false;
      textRef.current?.focus();
    }
  }, [openText]);

  const runCoach = async (answer: string) => {
    if (!coach) return;
    const trimmed = answer.trim();
    if (trimmed.length < 2) return;
    if (coachedRef.current === trimmed) return;
    coachedRef.current = trimmed;

    setThinking(true);
    const next = await coachAnswer({
      field,
      question: askedRef.current,
      answer: trimmed,
      priorFollowUps: followUpsRef.current,
    });
    if (!aliveRef.current) return;

    setThinking(false);
    onQuality?.(next.quality);
    if (next.follow_up) {
      followUpsRef.current += 1;
      askedRef.current = next.follow_up;
    }
    setVerdict(next);
  };

  const handleCapture = async (audio: Blob, seconds: number) => {
    setNotice(null);
    setVerdict(null);
    setTranscribing(true);
    try {
      const text = await transcribeAnswer(audio, seconds);
      if (!aliveRef.current) return;
      const merged = appendAnswer(value, text, maxLength);
      onChange(merged, 'voice');
      setOpenText(true);
      setTranscribing(false);
      await runCoach(merged);
    } catch (err) {
      if (!aliveRef.current) return;
      setTranscribing(false);
      // La transcripción se cae de siete maneras y ninguna es culpa de quien
      // habló: se dice qué pasó y se abre el teclado, que es la salida.
      setNotice(err instanceof Error ? err.message : t.voice.recordingFailed);
      setOpenText(true);
    }
  };

  const recorder = useVoiceRecorder({ onCapture: handleCapture });
  const { recording, seconds, problem } = recorder;

  // El micrófono no se pudo usar: el teclado se abre SOLO. Dejar a alguien
  // frente a un botón que no funciona y un enlace que tiene que descubrir es la
  // forma más cara de perder una postulación.
  useEffect(() => {
    if (problem) setOpenText(true);
  }, [problem]);

  const openWriting = () => {
    focusOnOpenRef.current = true;
    setOpenText(true);
  };

  const remaining = MAX_RECORDING_SECONDS - seconds;
  // El tope del campo se dice en voz alta cuando se acerca: un `maxLength` mudo
  // deja el teclado sin escribir y el dictado cortado, y quien lo vive cree que
  // la página se dañó (10-20, campo de capacidad física).
  const charsLeft = maxLength - value.length;
  const recorderNote = notice ?? problemNote(problem, t.voice.problem);
  const cheer = verdict?.quality === 'rich' ? verdict.encouragement : null;

  return (
    <div className="voice" data-field={dataField ?? field} tabIndex={-1}>
      <p className="voice__invite">{invitation}</p>

      {supported && problem !== 'denied' && problem !== 'unsupported' && (
        <div className="voice__stage">
          {transcribing ? (
            <WaitingInline label={t.voice.transcribing} />
          ) : (
            <>
              <button
                type="button"
                className={`mic${recording ? ' mic--live' : ''}`}
                aria-label={recording ? t.voice.stop : t.voice.record}
                aria-pressed={recording}
                onClick={() => (recording ? recorder.stop() : void recorder.start())}
              >
                <span className="mic__halo" aria-hidden="true" />
                <Icon name={recording ? 'stop' : 'mic'} size={30} />
              </button>

              {recording ? (
                <div className="voice__live" role="status" aria-live="off">
                  <span className="voice__wave" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                    <i />
                    <i />
                  </span>
                  <span className="voice__timer">{formatSeconds(seconds)}</span>
                </div>
              ) : (
                <p className="voice__cue">
                  {value.trim() ? t.voice.addMore : t.voice.tapAndTalk}
                </p>
              )}

              {/* Avisar a los 75 s, no cortar en seco a los 90: quien está a la
                  mitad de una frase alcanza a cerrarla. */}
              {recording && seconds >= WARN_RECORDING_SECONDS && (
                <p className="text-xs text-friction">
                  {remaining > 0 ? t.voice.secondsLeft(remaining) : t.voice.timeUp}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {recorderNote && (
        <p className="voice__note" role="status">
          {recorderNote}
        </p>
      )}

      {openText ? (
        <textarea
          id={id}
          ref={textRef}
          className={`textarea w-full${invalid ? ' textarea-error' : ''}`}
          rows={rows}
          maxLength={maxLength}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value, 'text')}
          onBlur={() => void runCoach(value)}
        />
      ) : (
        <button type="button" className="voice__write" onClick={openWriting}>
          {t.voice.preferWriting}
        </button>
      )}

      {openText && charsLeft <= CHARS_LEFT_WARNING && (
        <p className="text-xs text-friction" role="status">
          {t.voice.charsLeft(Math.max(0, charsLeft))}
        </p>
      )}

      {footer}

      {thinking && (
        <p className="voice__thinking" role="status">
          <span className="loading loading-dots loading-sm" />
          {t.voice.thinking}
        </p>
      )}

      {verdict?.follow_up && (
        <div className="coach rise">
          <img className="coach__face" src="/brand/mascot-96.png" alt="" width={36} height={36} />
          <div className="coach__bubble">
            <p className="coach__ask">{verdict.follow_up}</p>
            <p className="coach__hint">
              {supported && problem !== 'denied'
                ? t.voice.coachHintMic
                : t.voice.coachHintText}
            </p>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setVerdict(null)}
            >
              {t.voice.continueAsIs}
            </button>
          </div>
        </div>
      )}

      {cheer && (
        <p className="voice__cheer">
          <Icon name="check" size={14} />
          {cheer}
        </p>
      )}
    </div>
  );
}

/** El problema del aparato, dicho sin drama y con la salida en la misma frase. */
function problemNote(
  problem: RecorderProblem,
  text: Messages['screening']['voice']['problem'],
): string | null {
  if (problem === 'denied') return text.denied;
  if (problem === 'unsupported') return text.unsupported;
  if (problem === 'failed') return text.failed;
  if (problem === 'empty') return text.empty;
  return null;
}
